import type React from "react";

import { RoomType } from "@eisox/api/core-api";
import { Button, CanAccess, Field, Typography } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { ArrowRightIcon } from "@eisox/icons";
import { getDirtyValues, useMediaQuery } from "@eisox/tools";
import { z } from "@eisox/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { roomApi } from "~/api";
import { DeleteDialog } from "~/components/delete-dialog";
import { HybridTooltip } from "~/components/hybrid-tooltip";
import { MoveItemDialog } from "~/components/move-item-dialog";
import { AVAILABLE_ROOM_TYPES } from "~/constants/room.constant";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import { useRoomContext } from "~/providers/room.provider";

import { getRoomDeletionBlockers, type RoomDeletionBlocker } from "../helpers";

const NAME = "GeneralSettings";

export const GeneralSettings: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { building, permissions, rooms, floors, airHandlerUnits } = useBuildingContext(NAME);
  const { room, valves, multisensors } = useRoomContext("GeneralSettings");

  const { mutate: remove } = useMutation({
    meta: { toast: { action: "delete" } },
    mutationFn: () => roomApi.delete(room.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomApi.queries.building(building.id).queryKey });
      queryClient.removeQueries({ queryKey: roomApi.queries.plannings(room.id).queryKey });
      await navigate({ to: "/buildings/$buildingId/floors", params: { buildingId: building.id } });
    },
  });

  const { mutateAsync: updateRoom } = useMutation({
    mutationFn: (data: Partial<{ name: string; type: RoomType }>) =>
      roomApi.update(building.id, [{ ids: [room.id], ...data }]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomApi.queries.building(building.id).queryKey });
    },
  });

  const form = useAppForm({
    defaultValues: {
      name: room.name,
      type: room.type,
    },
    validators: {
      onSubmit: z.object({
        name: z.string().nonempty(),
        type: z.enum(RoomType),
      }),
    },
    onSubmit: async ({ value, formApi }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      await updateRoom(dirty);
      formApi.reset();
    },
  });

  const typeOptions = AVAILABLE_ROOM_TYPES.map(type => ({
    value: type,
    name: m.room_type({ type }),
  }));

  const deletionBlockers = getRoomDeletionBlockers({ room, valves, multisensors });
  const blockerMessages: Record<RoomDeletionBlocker, () => string> = {
    valves: () => m.delete_room_blocked_valves(),
    multisensors: () => m.delete_room_blocked_multisensors(),
    airHandlerUnits: () =>
      m.delete_room_blocked_air_handler_units({
        count: room.airHandlerUnitIds.length,
        names: room.airHandlerUnitIds.map(id => airHandlerUnits.find(ahu => ahu.id === id)?.name ?? id).join(", "),
      }),
  };

  return (
    <form
      className="flex h-full flex-col justify-between gap-[10px]"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="flex h-full flex-col gap-[20px]">
          <form.AppField name="name">
            {field => (
              <field.TextField
                classNames={{ label: "text-primary-blue", control: "w-full" }}
                label={m.name()}
                disabled={!permissions.room?.name?.update}
              />
            )}
          </form.AppField>
          <form.AppField name="type">
            {field => (
              <field.Select
                classNames={{ label: "text-primary-blue", control: "w-full" }}
                label={m.type()}
                multiple={false}
                options={typeOptions}
                disabled={!permissions.room?.type?.update}
              />
            )}
          </form.AppField>
          <CanAccess isAuthorized={permissions.room?.floor?.update && !isMobile}>
            <MoveItemDialog
              buildingId={building.id}
              type="room"
              defaultSelectedItemId={room.id}
              items={rooms}
              floors={floors}
            >
              <Field.Root>
                <Field.Label className="text-primary-blue">{m.to_position()}</Field.Label>
                <Button className="text-primary-dark-blue">
                  {m.move()}
                  <ArrowRightIcon />
                </Button>
              </Field.Root>
            </MoveItemDialog>
          </CanAccess>
          <div className="flex items-center justify-center gap-[20px]">
            <CanAccess isAuthorized={permissions.room?.delete}>
              {deletionBlockers.length > 0 ? (
                <HybridTooltip
                  content={
                    <div className="flex flex-col">
                      <span className="font-semibold">{m.delete_impossible()}</span>
                      {deletionBlockers.map(blocker => (
                        <span key={blocker}>{blockerMessages[blocker]()}</span>
                      ))}
                    </div>
                  }
                >
                  {/* A disabled button fires no pointer events, so the span carries the tooltip (hover and tap) */}
                  <span className="inline-flex" tabIndex={0}>
                    <Button className="pointer-events-none text-primary-red" disabled>
                      {m.delete()}
                    </Button>
                  </span>
                </HybridTooltip>
              ) : (
                <DeleteDialog
                  title={m.delete_room()}
                  onDelete={() => remove()}
                  description={
                    <Typography className="mb-[10px]">{m.confirm_deletion({ resource: room.name })}</Typography>
                  }
                >
                  <Button className="text-primary-red">{m.delete()}</Button>
                </DeleteDialog>
              )}
            </CanAccess>
            <form.SubmitButton label={m.save()} />
          </div>
        </div>
      </form.AppForm>
    </form>
  );
};

GeneralSettings.displayName = NAME;
